import { useCallback } from "react";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { logSalesTaxCreated } from "@/common/helpers/salesTaxTracking";
import { trpc } from "@/common/helpers/trpc";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useSalesTaxAvailability } from "@/common/hooks/useSalesTaxAvailability";
import { RouterOutputs } from "@/common/types/trpc";
import { normalizeTaxName } from "@/features/ManageCollectableTaxes/helpers/useManageCollectableTaxes";

export type TicketingTax = NonNullable<RouterOutputs["listOrganizationTaxes"]>[number];

export interface TaxUpsertInput {
  name: string;
  ratePpm: number;
  taxRegistrationNumber: string | null;
}

interface CreateTaxResult {
  tax?: TicketingTax;
  errorMessage?: string;
}

interface UpdateTaxResult {
  tax?: TicketingTax;
  errorMessage?: string;
}

interface DeleteTaxResult {
  deletedId?: string;
  errorMessage?: string;
}

interface UseTicketingTaxesReturn {
  taxes: TicketingTax[];
  getTaxById: (id: string) => TicketingTax | undefined;
  createTax: (input: TaxUpsertInput) => Promise<CreateTaxResult>;
  updateTax: (taxId: string, input: TaxUpsertInput) => Promise<UpdateTaxResult>;
  deleteTax: (taxId: string) => Promise<DeleteTaxResult>;
  isSaving: boolean;
  isDeleting: boolean;
}

export const useTicketingTaxes = (): UseTicketingTaxesReturn => {
  const { isSalesTaxManagementAvailable } = useSalesTaxAvailability();
  const { organization } = useCurrentUserContext();
  const { logAmplitudeEvent } = useAmplitude();
  const utils = trpc.useUtils();

  const { data } = trpc.listOrganizationTaxes.useQuery(undefined, { enabled: isSalesTaxManagementAvailable });
  const taxes = data ?? [];

  const { mutateAsync: saveTaxes, isPending: isSaving } = trpc.saveOrganizationTaxes.useMutation();
  const { mutateAsync: deleteTaxMutate, isPending: isDeleting } = trpc.deleteOrganizationTax.useMutation();

  const getTaxById = useCallback((id: string) => (data ?? []).find((tax) => tax.id === id), [data]);

  const refetchAndFindByName = useCallback(
    async (name: string): Promise<TicketingTax | undefined> => {
      await utils.listOrganizationTaxes.invalidate();
      const refreshed = await utils.listOrganizationTaxes.fetch();
      return refreshed?.find((tax) => normalizeTaxName(tax.name) === normalizeTaxName(name));
    },
    [utils.listOrganizationTaxes]
  );

  const createTax = useCallback(
    async (input: TaxUpsertInput): Promise<CreateTaxResult> => {
      const { error } = await saveTaxes({ taxes: [{ ...input, id: undefined }] });
      if (error) {
        return { errorMessage: error.message };
      }
      logSalesTaxCreated({
        logAmplitudeEvent,
        organizationId: organization?.id,
        source: "form_editor",
        ratePpm: input.ratePpm,
      });
      const tax = await refetchAndFindByName(input.name);
      return { tax };
    },
    [logAmplitudeEvent, organization?.id, refetchAndFindByName, saveTaxes]
  );

  const updateTax = useCallback(
    async (taxId: string, input: TaxUpsertInput): Promise<UpdateTaxResult> => {
      const { error } = await saveTaxes({ taxes: [{ ...input, id: taxId }] });
      if (error) {
        return { errorMessage: error.message };
      }
      await utils.listOrganizationTaxes.invalidate();
      const refreshed = await utils.listOrganizationTaxes.fetch();
      return { tax: refreshed?.find((tax) => tax.id === taxId) };
    },
    [saveTaxes, utils.listOrganizationTaxes]
  );

  const deleteTax = useCallback(
    async (taxId: string): Promise<DeleteTaxResult> => {
      const { error } = await deleteTaxMutate({ taxId });
      if (error) {
        return { errorMessage: error.message };
      }
      await utils.listOrganizationTaxes.invalidate();
      return { deletedId: taxId };
    },
    [deleteTaxMutate, utils.listOrganizationTaxes]
  );

  return { taxes, getTaxById, createTax, updateTax, deleteTax, isSaving, isDeleting };
};
